import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  estimateApi,
  COST_ESTIMATE_OVERWRITE_REQUIRED,
  type Estimate,
  type EstimateSummary,
  type CreateEstimateRequest,
  type MeasurementSystem,
  type UpdateEstimateRequest,
} from '../api/estimate-service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useEstimate');

/** Status values for which the estimate is still being generated. */
const IN_PROGRESS_STATUSES = new Set(['pending', 'generating']);

/**
 * Concept-scoped list of estimates. The editor renders the "current" estimate
 * (last ready, else most recent) — the full carousel/list is FE3.
 */
export function useConceptEstimates(projectId: string, conceptId: string) {
  return useQuery({
    queryKey: cacheKeys.estimates.list(conceptId),
    queryFn: () => estimateApi.list(projectId, conceptId),
    enabled: Boolean(projectId && conceptId),
    staleTime: 30 * 1000,
  });
}

/**
 * Project-scoped list of every estimate across all concepts. Powers the
 * dedicated project Estimates page (FE3). `created_at` order comes from the
 * server (in-progress on top, then newest first).
 */
export function useProjectEstimates(projectId: string) {
  return useQuery({
    queryKey: cacheKeys.estimates.projectList(projectId),
    queryFn: () => estimateApi.listByProject(projectId),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

/**
 * Pick the estimate the editor should show: the most recent `ready` one, else
 * the most recent of any status (so a pending/generating/failed estimate is
 * still surfaced). `created_at` descending.
 */
export function selectCurrentEstimate(
  estimates: EstimateSummary[] | undefined
): EstimateSummary | undefined {
  if (!estimates || estimates.length === 0) return undefined;
  const byNewest = [...estimates].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  return byNewest.find((e) => e.status === 'ready') ?? byNewest[0];
}

/**
 * Full estimate (with line items) for the editor. Polls while
 * pending/generating as a safety net behind the WS `estimates` events.
 */
export function useEstimateDetail(estimateId: string | undefined) {
  return useQuery({
    queryKey: estimateId
      ? cacheKeys.estimates.detail(estimateId)
      : ['estimates', 'detail', 'none'],
    queryFn: () => estimateApi.get(estimateId as string),
    enabled: Boolean(estimateId),
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data as Estimate | undefined;
      return data && IN_PROGRESS_STATUSES.has(data.status) ? 4000 : false;
    },
  });
}

/**
 * Create + regenerate mutations and header (title/notes/duration) updates for
 * a single estimate. Item-level mutations live in `use-estimate-items.ts`.
 */
export function useEstimateActions(projectId: string, conceptId: string) {
  const queryClient = useQueryClient();
  // Server-driven paywall — cost estimate is a Pro perk; a 422/403 limit error
  // routes to the shared LimitPaywallDialog with server-owned copy.
  const routeLimitErrorToPaywall = useLimitErrorPaywall();

  const createMutation = useMutation({
    mutationFn: (data: CreateEstimateRequest) =>
      estimateApi.create(projectId, conceptId, data),
    onSuccess: () => {
      // WS `estimates/created` will reconcile; invalidate immediately so the
      // pending estimate appears without waiting for the socket.
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiClientError && routeLimitErrorToPaywall(error)) {
        return;
      }
      logger.error('Estimate generation failed', { conceptId, error });
      const message =
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to generate estimate';
      toast.error(message);
    },
  });

  /**
   * Regenerate. When the estimate was hand-edited the server rejects with 409
   * `COST_ESTIMATE_OVERWRITE_REQUIRED` unless `confirm_overwrite` is set. The
   * caller catches that signal to open a confirm dialog, then retries with
   * `confirmOverwrite=true`.
   */
  const regenerateMutation = useMutation({
    mutationFn: ({
      estimateId,
      confirmOverwrite,
    }: {
      estimateId: string;
      confirmOverwrite?: boolean;
    }) => estimateApi.regenerate(estimateId, confirmOverwrite),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.detail(variables.estimateId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
    },
    onError: (error: unknown) => {
      // Overwrite-required is a handled UX branch, not a failure — swallow here
      // so the caller can open the confirm dialog without a toast.
      if (
        error instanceof ApiClientError &&
        error.code === COST_ESTIMATE_OVERWRITE_REQUIRED
      ) {
        return;
      }
      if (error instanceof ApiClientError && routeLimitErrorToPaywall(error)) {
        return;
      }
      logger.error('Estimate regenerate failed', { conceptId, error });
      const message =
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to regenerate estimate';
      toast.error(message);
    },
  });

  /**
   * Patch header fields with an optimistic cache write. The server recomputes
   * nothing on these fields, so the response is the source of truth and we
   * reconcile on settle.
   */
  const updateMutation = useMutation({
    mutationFn: ({
      estimateId,
      data,
    }: {
      estimateId: string;
      data: UpdateEstimateRequest;
    }) => estimateApi.update(estimateId, data),
    onMutate: async ({ estimateId, data }) => {
      const key = cacheKeys.estimates.detail(estimateId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Estimate>(key);
      if (previous) {
        queryClient.setQueryData<Estimate>(key, { ...previous, ...data });
      }
      return { previous, key };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      logger.error('Estimate update failed', { error });
    },
    onSuccess: (estimate) => {
      queryClient.setQueryData(
        cacheKeys.estimates.detail(estimate.id),
        estimate
      );
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
    },
  });

  /**
   * Switch the estimate's display currency. Deliberately NOT the optimistic
   * `updateMutation`: a currency switch changes every amount server-side, so an
   * optimistic header write would briefly show the new currency symbol next to
   * the old (unconverted) numbers. Instead the cache is only written from the
   * full converted response.
   */
  const changeCurrencyMutation = useMutation({
    mutationFn: ({
      estimateId,
      currency,
    }: {
      estimateId: string;
      currency: string;
    }) => estimateApi.update(estimateId, { currency }),
    onSuccess: (estimate) => {
      queryClient.setQueryData(
        cacheKeys.estimates.detail(estimate.id),
        estimate
      );
      // List rows carry `currency` + `grand_total` — refresh both scopes.
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.projectList(projectId),
      });
    },
    onError: (error) => {
      logger.error('Estimate currency change failed', { error });
      const message =
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to change currency';
      toast.error(message);
    },
  });

  /**
   * Switch the estimate's display measurement system (metric ↔ imperial).
   * Same shape as the currency switch and deliberately NOT optimistic: the
   * server converts every quantity, unit code, and unit price, so the cache is
   * only written from the full converted response.
   */
  const changeMeasurementSystemMutation = useMutation({
    mutationFn: ({
      estimateId,
      measurementSystem,
    }: {
      estimateId: string;
      measurementSystem: MeasurementSystem;
    }) =>
      estimateApi.update(estimateId, { measurement_system: measurementSystem }),
    onSuccess: (estimate) => {
      queryClient.setQueryData(
        cacheKeys.estimates.detail(estimate.id),
        estimate
      );
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.projectList(projectId),
      });
    },
    onError: (error) => {
      logger.error('Estimate measurement system change failed', { error });
      const message =
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to change measurement system';
      toast.error(message);
    },
  });

  /**
   * Switch the estimate's output language. A generation input (no figure
   * changes — existing texts keep their language until the next regenerate),
   * but kept as a dedicated non-optimistic mutation so the settings row gets
   * a spinner + error toast, mirroring the currency/measurement switches.
   */
  const changeLanguageMutation = useMutation({
    mutationFn: ({
      estimateId,
      language,
    }: {
      estimateId: string;
      language: string;
    }) => estimateApi.update(estimateId, { language }),
    onSuccess: (estimate) => {
      queryClient.setQueryData(
        cacheKeys.estimates.detail(estimate.id),
        estimate
      );
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
    },
    onError: (error) => {
      logger.error('Estimate language change failed', { error });
      const message =
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to change language';
      toast.error(message);
    },
  });

  /**
   * Delete the whole estimate (concept-page kebab → "Delete estimate"). The
   * tab falls back to the intro screen once the list cache no longer holds a
   * current estimate; other devices reconcile via WS `estimates/deleted`.
   */
  const deleteMutation = useMutation({
    mutationFn: (estimateId: string) => estimateApi.delete(estimateId),
    onSuccess: (_data, estimateId) => {
      queryClient.removeQueries({
        queryKey: cacheKeys.estimates.detail(estimateId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.list(conceptId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.estimates.projectList(projectId),
      });
    },
    onError: (error: unknown) => {
      logger.error('Estimate delete failed', { conceptId, error });
      const message =
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to delete estimate';
      toast.error(message);
    },
  });

  const updateField = useCallback(
    (estimateId: string, data: UpdateEstimateRequest) =>
      updateMutation.mutate({ estimateId, data }),
    [updateMutation]
  );

  const changeCurrency = useCallback(
    (estimateId: string, currency: string) =>
      changeCurrencyMutation.mutate({ estimateId, currency }),
    [changeCurrencyMutation]
  );

  const changeMeasurementSystem = useCallback(
    (estimateId: string, measurementSystem: MeasurementSystem) =>
      changeMeasurementSystemMutation.mutate({ estimateId, measurementSystem }),
    [changeMeasurementSystemMutation]
  );

  const changeLanguage = useCallback(
    (estimateId: string, language: string) =>
      changeLanguageMutation.mutate({ estimateId, language }),
    [changeLanguageMutation]
  );

  return {
    createEstimate: createMutation.mutate,
    isCreating: createMutation.isPending,
    regenerateEstimate: regenerateMutation.mutateAsync,
    isRegenerating: regenerateMutation.isPending,
    updateEstimate: updateField,
    changeCurrency,
    isChangingCurrency: changeCurrencyMutation.isPending,
    changeMeasurementSystem,
    isChangingMeasurement: changeMeasurementSystemMutation.isPending,
    changeLanguage,
    isChangingLanguage: changeLanguageMutation.isPending,
    deleteEstimate: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
